import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash2, Edit, Plus, Image as ImageIcon, GripVertical } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

const API = process.env.REACT_APP_BACKEND_URL;

export default function TreatmentsAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', content: '', image: '', order: 0 });

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/treatments`, { withCredentials: true });
      setItems(data.sort((a, b) => a.order - b.order));
    } catch (err) { toast.error('Veri yüklenemedi'); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await axios.post(`${API}/api/upload`, formData, { withCredentials: true });
      setForm({ ...form, image: data.url });
      toast.success('Görsel yüklendi');
    } catch (err) { toast.error('Yükleme başarısız'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(`${API}/api/admin/treatments/${editing.id}`, form, { withCredentials: true });
        toast.success('Güncellendi');
      } else {
        const payload = { ...form, order: items.length + 1 };
        await axios.post(`${API}/api/admin/treatments`, payload, { withCredentials: true });
        toast.success('Eklendi');
      }
      setEditing(null);
      setForm({ title: '', description: '', content: '', image: '', order: 0 });
      fetchItems();
    } catch (err) { toast.error('İşlem başarısız'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Emin misiniz?')) return;
    try {
      await axios.delete(`${API}/api/admin/treatments/${id}`, { withCredentials: true });
      toast.success('Silindi');
      fetchItems();
    } catch (err) { toast.error('Silinemedi'); }
  };

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const itemsCopy = Array.from(items);
    const [reorderedItem] = itemsCopy.splice(result.source.index, 1);
    itemsCopy.splice(result.destination.index, 0, reorderedItem);

    // Update UI immediately
    setItems(itemsCopy);

    // Prepare payload for backend
    const payload = itemsCopy.map((item, index) => ({ id: item.id, order: index + 1 }));

    try {
      await axios.put(`${API}/api/admin/treatments/reorder`, payload, { withCredentials: true });
      toast.success('Sıralama güncellendi');
    } catch (err) {
      toast.error('Sıralama kaydedilemedi, değişiklik geri alınıyor...');
      fetchItems(); // revert on fail
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-slate-900">Tedaviler</h1>
        <button 
          onClick={() => { setEditing(null); setForm({ title: '', description: '', content: '', image: '', order: items.length + 1 }); }}
          className="bg-primary text-white px-4 py-2 rounded-xl flex items-center gap-2 hover:bg-primary/90 transition-colors text-sm font-medium"
        >
          <Plus className="w-4 h-4" /> Yeni Ekle
        </button>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-1 bg-white p-6 rounded-2xl shadow-sm border border-slate-200 h-fit">
          <h2 className="text-lg font-bold mb-4">{editing ? 'Tedavi Düzenle' : 'Yeni Tedavi'}</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Başlık</label>
              <input type="text" value={form.title} onChange={e => setForm({...form, title: e.target.value})} required className="w-full px-3 py-2 border rounded-xl focus:ring-2 focus:ring-primary outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Kısa Açıklama</label>
              <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} required rows={2} className="w-full px-3 py-2 border rounded-xl focus:ring-2 focus:ring-primary outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Detaylı İçerik (Paragraflar)</label>
              <textarea value={form.content || ''} onChange={e => setForm({...form, content: e.target.value})} rows={6} className="w-full px-3 py-2 border rounded-xl focus:ring-2 focus:ring-primary outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Görsel</label>
              {form.image && <img src={form.image.startsWith('http') ? form.image : API + form.image} alt="Preview" className="h-20 w-auto object-cover rounded-xl mb-2" />}
              <label className="cursor-pointer bg-slate-100 text-slate-600 px-4 py-2 rounded-xl flex items-center justify-center gap-2 hover:bg-slate-200 transition-colors text-sm font-medium">
                <ImageIcon className="w-4 h-4" /> Görsel Yükle
                <input type="file" className="hidden" accept="image/*" onChange={handleUpload} />
              </label>
            </div>
            <div className="flex gap-2 pt-2">
              <button type="submit" className="flex-1 bg-primary text-white py-2 rounded-xl text-sm font-medium hover:bg-primary/90">Kaydet</button>
              {editing && <button type="button" onClick={() => setEditing(null)} className="flex-1 bg-slate-100 text-slate-700 py-2 rounded-xl text-sm font-medium hover:bg-slate-200">İptal</button>}
            </div>
          </form>
        </div>

        <div className="md:col-span-2 bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="bg-slate-50 border-b border-slate-100 text-slate-500 text-sm font-medium grid grid-cols-12 px-4 py-3">
            <div className="col-span-1"></div>
            <div className="col-span-3">Görsel</div>
            <div className="col-span-6">Başlık</div>
            <div className="col-span-2 text-right">İşlem</div>
          </div>
          
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="treatments-list">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef} className="divide-y divide-slate-100 min-h-[100px]">
                  {items.map((item, index) => (
                    <Draggable key={item.id} draggableId={item.id} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={`grid grid-cols-12 items-center px-4 py-3 text-sm text-slate-600 transition-colors ${snapshot.isDragging ? 'bg-primary/5 shadow-lg z-50 rounded-xl' : 'hover:bg-slate-50/50 bg-white'}`}
                        >
                          <div className="col-span-1 cursor-grab" {...provided.dragHandleProps} title="Sürükleyip Bırakarak Sırala">
                            <GripVertical className="w-5 h-5 text-slate-400 hover:text-slate-600" />
                          </div>
                          <div className="col-span-3">
                            <img src={item.image?.startsWith('http') ? item.image : API + item.image} alt="" className="w-12 h-12 rounded-lg object-cover bg-slate-100" />
                          </div>
                          <div className="col-span-6 font-medium text-slate-900">{item.title}</div>
                          <div className="col-span-2 text-right space-x-3">
                            <button onClick={() => { setEditing(item); setForm(item); }} className="text-blue-600 hover:text-blue-800"><Edit className="w-5 h-5 inline" /></button>
                            <button onClick={() => handleDelete(item.id)} className="text-red-500 hover:text-red-700"><Trash2 className="w-5 h-5 inline" /></button>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                  {items.length === 0 && <div className="p-8 text-center text-slate-500">Henüz tedavi eklenmemiş.</div>}
                </div>
              )}
            </Droppable>
          </DragDropContext>
          <div className="bg-slate-50 p-3 text-xs text-slate-500 text-center border-t border-slate-100">
            Sıralamayı değiştirmek için soldaki ikondan tutup sürükleyin.
          </div>
        </div>
      </div>
    </div>
  );
}
